import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Clock, Shield, Gamepad2, Sparkles } from 'lucide-react';
import { Input } from 'components/ui/input';
import Textarea from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Card, CardContent } from 'components/ui/card';
import Switch from 'components/ui/switch';
import { RadioGroup, RadioGroupItem } from 'components/ui/RadioGroup';
import { PageHeader } from 'components/ui/page-header';
import { SaveBar } from 'components/ui/save-bar';
import * as TestModule from 'modules/test';
import * as TestApi from 'modules/test/api';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';
import { PremiumBadge } from 'components/entitlements';
import { useToast } from 'components/ui/use-toast';
import { isValidTotalDuration, isValidPerQuestionDuration, formatMinutesAsHHMM } from 'lib/timeFormat';
import { useTranslation } from 'react-i18next';
import SettingRow from './settings/SettingRow';
import SettingsNav, { type SettingsNavItem } from './settings/SettingsNav';

const LANGUAGES: { value: string; labelKey: string }[] = [
  { value: 'uz', labelKey: 'lang_uzbek' },
  { value: 'ru', labelKey: 'lang_russian' },
  { value: 'en', labelKey: 'lang_english' },
];

const MINUTES_PER_DAY = 24 * 60;
const toInt = (v: string): number => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const pad = (n: number) => String(n).padStart(2, '0');
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (v: string): string | null => (v ? new Date(v).toISOString() : null);

type DurationMethod = 'total' | 'per_question';
type ActivationMethod = 'manual' | 'scheduled';

interface GeneralState {
  title: string;
  description: string;
  language: string;
  /** Learners may report a problem with a question. On unless the author switched it off. */
  questionReports: boolean;
}
interface TimingState {
  durationMethod: DurationMethod;
  timeToComplete: string;
  timeLimitPerQuestion: string;
  activationMethod: ActivationMethod;
  activeDays: string;
  activeHours: string;
  activeMinutes: string;
  availableFrom: string;
  availableUntil: string;
  allowFlexibleNavigation: boolean;
  showSubQuestionsInNavigator: boolean;
}
interface AntiCheatState {
  antiCheatingMonitor: boolean;
  disableRightClick: boolean;
  disableCopyPaste: boolean;
  shuffleAnswers: boolean;
  shuffleQuestions: boolean;
  lockdownBrowser: boolean;
}

type AiGradeMinConfidence = 'HIGH' | 'MEDIUM' | 'ANY';
const AI_GRADE_MIN_CONFIDENCES: ReadonlyArray<{ value: AiGradeMinConfidence; labelKey: string; descKey: string }> = [
  { value: 'HIGH', labelKey: 'ai_min_confidence_high', descKey: 'ai_min_confidence_high_desc' },
  { value: 'MEDIUM', labelKey: 'ai_min_confidence_medium', descKey: 'ai_min_confidence_medium_desc' },
  { value: 'ANY', labelKey: 'ai_min_confidence_any', descKey: 'ai_min_confidence_any_desc' },
];
const isAiGradeMinConfidence = (v: unknown): v is AiGradeMinConfidence =>
  AI_GRADE_MIN_CONFIDENCES.some((option) => option.value === v);

interface AiGradingState {
  autoGradeOpenQuestions: boolean;
  /** Lowest AI confidence accepted as the final grade; HIGH matches tests saved before this existed. */
  aiGradeMinConfidence: AiGradeMinConfidence;
  showAiFeedbackToCandidate: boolean;
  /** IELTS only: the examiner may ask a follow-up about a Part 1 / Part 3 answer. On unless the author switched it off. */
  liveExaminer: boolean;
}

const DEFAULT_ANTI: AntiCheatState = {
  antiCheatingMonitor: false,
  disableRightClick: false,
  disableCopyPaste: false,
  shuffleAnswers: false,
  shuffleQuestions: false,
  lockdownBrowser: false,
};

const DEFAULT_AI_GRADING: AiGradingState = {
  autoGradeOpenQuestions: false,
  aiGradeMinConfidence: 'HIGH',
  showAiFeedbackToCandidate: false,
  liveExaminer: true,
};

const eq = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Only *dirty* sections are PATCHed on Save, so no field resets to default. */
export const BasicSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const { hash } = useLocation();
  const { toast } = useToast();
  // Scheduled activation + availability windows are a paid capability (Growth+).
  const canScheduling = useFeature(FEATURES.TEST_SCHEDULING);
  // AI auto-grading runs only on plans with AI grading; the API skips it otherwise.
  const canAiGrading = useFeature(FEATURES.AI_GRADING);
  const openUpgrade = useOpenUpgradeForFeature();
  const queryClient = useQueryClient();

  const { item: test, isLoading, refetch } = TestModule.Hooks.useSingle({
    id: testId || '',
    enabled: Boolean(testId),
  });

  const [general, setGeneral] = useState<GeneralState>({ title: '', description: '', language: 'uz', questionReports: true });
  const [timing, setTiming] = useState<TimingState>({
    durationMethod: 'total',
    timeToComplete: '',
    timeLimitPerQuestion: '02:00',
    activationMethod: 'manual',
    activeDays: '',
    activeHours: '',
    activeMinutes: '',
    availableFrom: '',
    availableUntil: '',
    allowFlexibleNavigation: true,
    showSubQuestionsInNavigator: true,
  });
  const [anti, setAnti] = useState<AntiCheatState>(DEFAULT_ANTI);
  const [aiGrading, setAiGrading] = useState<AiGradingState>(DEFAULT_AI_GRADING);
  // Baselines for dirty detection + Discard.
  const baseline = useRef<{
    general: GeneralState;
    timing: TimingState;
    anti: AntiCheatState;
    aiGrading: AiGradingState;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  const sectionRefs = {
    general: useRef<HTMLElement>(null),
    timing: useRef<HTMLElement>(null),
    aiGrading: useRef<HTMLElement>(null),
    anticheat: useRef<HTMLElement>(null),
    learnerReports: useRef<HTMLElement>(null),
    gamification: useRef<HTMLElement>(null),
  };

  // Hydrate every section from the test, and snapshot the baseline.
  useEffect(() => {
    if (!test) return;
    const tt = test as Record<string, any>;
    const nextGeneral: GeneralState = {
      title: tt.title ?? '',
      description: tt.description ?? '',
      language: tt.language ?? 'uz',
      questionReports: tt.settings?.questionReports !== false,
    };

    const time = tt.config?.time ?? {};
    const canonicalMinutes = Number(tt.settings?.timeLimit);
    let timeToComplete = '';
    if (Number.isFinite(canonicalMinutes) && canonicalMinutes > 0) timeToComplete = formatMinutesAsHHMM(canonicalMinutes);
    else if (time.timeToComplete) timeToComplete = time.timeToComplete;
    // Never configured (undefined) OR an explicit 0 both mean "active until you
    // finish it manually" — hydrate empty so an unrelated timing edit can't
    // silently materialize a window default and start auto-closing the test.
    const storedWindow = Number(time.manualActivationDurationMinutes);
    const windowMinutes = Number.isFinite(storedWindow) && storedWindow > 0 ? storedWindow : 0;
    const nextTiming: TimingState = {
      durationMethod: (time.durationMethod as DurationMethod) ?? 'total',
      timeToComplete,
      timeLimitPerQuestion: time.timeLimitPerQuestion ?? '02:00',
      activationMethod: (time.activationMethod as ActivationMethod) ?? 'manual',
      activeDays: windowMinutes ? String(Math.floor(windowMinutes / MINUTES_PER_DAY)) : '',
      activeHours: windowMinutes ? String(Math.floor((windowMinutes % MINUTES_PER_DAY) / 60)) : '',
      activeMinutes: windowMinutes ? String(windowMinutes % 60) : '',
      availableFrom: toLocalInput(tt.availableFrom),
      availableUntil: toLocalInput(tt.availableUntil),
      allowFlexibleNavigation: time.allowFlexibleNavigation ?? true,
      showSubQuestionsInNavigator: time.showSubQuestionsInNavigator ?? true,
    };

    // Whitelist-pick only the known anti-cheating keys; retired keys from older
    // DTOs must not be spread back (backend forbidNonWhitelisted → 400).
    const savedAnti = (tt.antiCheating ?? {}) as Record<string, unknown>;
    const nextAnti: AntiCheatState = { ...DEFAULT_ANTI };
    (Object.keys(DEFAULT_ANTI) as Array<keyof AntiCheatState>).forEach((key) => {
      if (typeof savedAnti[key] === 'boolean') nextAnti[key] = savedAnti[key] as boolean;
    });

    const savedSettings = (tt.settings ?? {}) as Record<string, unknown>;
    const nextAiGrading: AiGradingState = {
      autoGradeOpenQuestions: Boolean(savedSettings.autoGradeOpenQuestions),
      aiGradeMinConfidence: isAiGradeMinConfidence(savedSettings.aiGradeMinConfidence)
        ? savedSettings.aiGradeMinConfidence
        : DEFAULT_AI_GRADING.aiGradeMinConfidence,
      showAiFeedbackToCandidate: Boolean(savedSettings.showAiFeedbackToCandidate),
      liveExaminer: savedSettings.liveExaminer !== false,
    };

    setGeneral(nextGeneral);
    setTiming(nextTiming);
    setAnti(nextAnti);
    setAiGrading(nextAiGrading);
    baseline.current = {
      general: nextGeneral,
      timing: nextTiming,
      anti: nextAnti,
      aiGrading: nextAiGrading,
    };
  }, [test]);

  // Scroll-spy: highlight the section nearest the top of the viewport.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target instanceof HTMLElement && visible.target.dataset.section) {
          setActiveSection(visible.target.dataset.section);
        }
      },
      { rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );
    Object.values(sectionRefs).forEach((ref) => ref.current && observer.observe(ref.current));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // Deep-link to a section via #hash (old /time, /anti-cheating routes redirect here).
  useEffect(() => {
    if (isLoading) return;
    const id = hash.replace('#', '');
    if (id && sectionRefs[id as keyof typeof sectionRefs]?.current) {
      sectionRefs[id as keyof typeof sectionRefs].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, isLoading]);

  const scrollTo = useCallback((id: string) => {
    setActiveSection(id);
    sectionRefs[id as keyof typeof sectionRefs]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IELTS-style / Digital SAT practice tests are timed per section on the
  // Questions step; only the activation window is configured here.
  const isExamTest = Boolean((test as { examConfig?: unknown } | undefined)?.examConfig);
  const isIeltsTest = (test as { examConfig?: { standard?: string } } | undefined)?.examConfig?.standard === 'IELTS';
  const isPerQuestion = timing.durationMethod === 'per_question';
  const totalInvalid = !isExamTest && timing.durationMethod === 'total' && !isValidTotalDuration(timing.timeToComplete);
  const perQuestionInvalid = !isExamTest && isPerQuestion && !isValidPerQuestionDuration(timing.timeLimitPerQuestion);
  const windowInvalid =
    timing.activationMethod === 'scheduled' &&
    Boolean(timing.availableFrom) &&
    Boolean(timing.availableUntil) &&
    new Date(timing.availableUntil).getTime() <= new Date(timing.availableFrom).getTime();
  // Scheduling is Growth+; the radio/toggle handlers below already prevent a
  // non-entitled user from ever selecting "scheduled" client-side, but this is
  // the defense-in-depth fallback for Save itself (e.g. a stale hydrated value
  // from before a plan downgrade) — the backend FeatureGuard remains the real
  // enforcement point.
  const effectiveActivationMethod: ActivationMethod = canScheduling ? timing.activationMethod : 'manual';

  const base = baseline.current;
  const dirtyGeneral = base ? !eq(general, base.general) : false;
  const dirtyTiming = base ? !eq(timing, base.timing) : false;
  const dirtyAnti = base ? !eq(anti, base.anti) : false;
  const dirtyAi = base ? !eq(aiGrading, base.aiGrading) : false;
  const dirty = dirtyGeneral || dirtyTiming || dirtyAnti || dirtyAi;
  const saveDisabled = saving || totalInvalid || perQuestionInvalid || windowInvalid;

  // Warn on tab close/refresh/external navigation while any section has
  // unsaved edits. In-app route-change guarding (a react-router navigation
  // blocker) is a bigger change and intentionally out of scope here.
  useEffect(() => {
    if (!dirty) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [dirty]);

  const updateTiming = <K extends keyof TimingState>(key: K, value: TimingState[K]) =>
    setTiming((prev) => ({ ...prev, [key]: value }));
  const updateAnti = (key: keyof AntiCheatState, value: boolean) => setAnti((prev) => ({ ...prev, [key]: value }));
  const updateAi = <K extends keyof AiGradingState>(key: K, value: AiGradingState[K]) =>
    setAiGrading((prev) => ({ ...prev, [key]: value }));

  const handleDiscard = () => {
    if (!base) return;
    setGeneral(base.general);
    setTiming(base.timing);
    setAnti(base.anti);
    setAiGrading(base.aiGrading);
  };

  const handleSave = async () => {
    if (!testId || saveDisabled || !dirty) return;
    setSaving(true);
    try {
      if (dirtyGeneral) {
        await TestApi.UpdateConfigBasic(testId, {
          title: general.title || t('new_test'),
          // Send the raw value (incl. empty) so clearing the description actually
          // blanks it — `|| undefined` would make the backend skip it and restore
          // the old value on refetch.
          description: general.description,
          language: general.language,
        });
        // A settings key, so it goes through the settings merge and only when it changed: the basic call above
        // never carries it, and a pre-save copy of the other settings must not be written back.
        if (general.questionReports !== base?.general.questionReports) {
          await TestApi.Update({ id: testId, settings: { questionReports: general.questionReports } });
        }
      }
      if (dirtyTiming) {
        const manualWindowMinutes =
          toInt(timing.activeDays) * MINUTES_PER_DAY + toInt(timing.activeHours) * 60 + toInt(timing.activeMinutes);
        await TestApi.UpdateConfigTime(testId, {
          ...(isExamTest
            ? {}
            : {
                durationMethod: timing.durationMethod,
                timeToComplete: timing.durationMethod === 'total' ? timing.timeToComplete : undefined,
                timeLimitPerQuestion: isPerQuestion ? timing.timeLimitPerQuestion : undefined,
                allowFlexibleNavigation: isPerQuestion ? false : timing.allowFlexibleNavigation,
                showSubQuestionsInNavigator: timing.showSubQuestionsInNavigator,
              }),
          activationMethod: effectiveActivationMethod,
          manualActivationDurationMinutes: effectiveActivationMethod === 'manual' ? manualWindowMinutes : undefined,
        });
        if (effectiveActivationMethod === 'scheduled') {
          // Same top-level fields the Access page edits — one source of truth.
          await TestApi.UpdatePublicLink(testId, {
            availableFrom: fromLocalInput(timing.availableFrom),
            availableUntil: fromLocalInput(timing.availableUntil),
          });
        }
      }
      if (dirtyAnti) {
        await TestApi.UpdateConfigAntiCheating(testId, { ...anti } as unknown as Record<string, unknown>);
      }
      if (dirtyAi) {
        // Send only the keys this section owns: the API merges settings over the
        // stored doc, and a pre-save copy would revert settings.timeLimit written
        // by the time save above.
        await TestApi.Update({
          id: testId,
          settings: {
            autoGradeOpenQuestions: aiGrading.autoGradeOpenQuestions,
            aiGradeMinConfidence: aiGrading.aiGradeMinConfidence,
            showAiFeedbackToCandidate: aiGrading.showAiFeedbackToCandidate,
            ...(isIeltsTest ? { liveExaminer: aiGrading.liveExaminer } : {}),
          },
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['test', 'single', testId] });
      queryClient.invalidateQueries({ queryKey: ['test', 'list'] });
      await refetch();
      toast.success(t('saved'));
    } catch {
      toast.error(t('error'));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !test) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  const navItems: SettingsNavItem[] = [
    { id: 'general', label: t('settings_section_general') },
    { id: 'timing', label: t('settings_section_timing') },
    { id: 'aiGrading', label: t('ai_auto_grading') },
    { id: 'anticheat', label: t('settings_section_anticheat') },
    { id: 'learnerReports', label: t('question_reports.setting_section') },
    { id: 'gamification', label: t('gamification') },
  ];

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title={t('test_settings')} className="mb-6" />

      <div className="flex gap-8">
        <SettingsNav items={navItems} activeId={activeSection} onSelect={scrollTo} heading={t('on_this_page')} />

        <div className="min-w-0 flex-1 space-y-6 pb-6">
          {/* General */}
          <section ref={sectionRefs.general} data-section="general" id="general" className="scroll-mt-4">
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-2 text-sm font-semibold text-foreground">{t('settings_section_general')}</h2>
                <SettingRow label={t('test_name')} htmlFor="test-name" stacked>
                  <Input
                    id="test-name"
                    value={general.title}
                    onChange={(e) => setGeneral((p) => ({ ...p, title: e.target.value }))}
                    placeholder={t('new_test')}
                    className="max-w-md"
                  />
                </SettingRow>
                <SettingRow
                  label={t('description_optional_notes_visible_only_to_you')}
                  description={t('add_test_description_for_identification_purposes_it_will_be_visible_to_you_only')}
                  htmlFor="test-description"
                  stacked
                >
                  <Textarea
                    id="test-description"
                    value={general.description}
                    onChange={(e) => setGeneral((p) => ({ ...p, description: e.target.value }))}
                    placeholder={t('add_test_description_for_identification_purposes')}
                    rows={3}
                    className="max-w-md"
                  />
                </SettingRow>
                <SettingRow label={t('test_language')} description={t('select_test_language')} htmlFor="test-language" stacked>
                  <Select value={general.language} onValueChange={(v) => setGeneral((p) => ({ ...p, language: v ?? 'uz' }))}>
                    <SelectTrigger className="max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SettingRow>
              </CardContent>
            </Card>
          </section>

          {/* Timing */}
          <section ref={sectionRefs.timing} data-section="timing" id="timing" className="scroll-mt-4">
            <Card>
              <CardContent className="space-y-4 p-6">
                <h2 className="text-sm font-semibold text-foreground">{t('settings_section_timing')}</h2>
                {isExamTest ? (
                  <p className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">{t('exam.settings.timed_per_section')}</p>
                ) : (
                <>
                <p className="text-sm text-muted-foreground">{t('select_test_duration_measuring_method')}</p>
                <RadioGroup
                  value={timing.durationMethod}
                  onValueChange={(v) => updateTiming('durationMethod', v as DurationMethod)}
                  name="duration"
                  className="gap-3"
                >
                  <div>
                    <label htmlFor="duration-total" className="flex cursor-pointer items-center gap-3">
                      <RadioGroupItem id="duration-total" value="total" />
                      <span className="text-sm">{t('time_to_complete_the_test_hh_mm')}</span>
                      <Input
                        value={timing.timeToComplete}
                        onChange={(e) => updateTiming('timeToComplete', e.target.value)}
                        placeholder={t('time_placeholder_hh_mm')}
                        className={`w-24 ${totalInvalid ? 'border-destructive' : ''}`}
                        disabled={timing.durationMethod !== 'total'}
                        aria-invalid={totalInvalid}
                      />
                    </label>
                    {totalInvalid && <p className="ml-7 mt-1 text-sm text-destructive">{t('time_invalid_hhmm')}</p>}
                  </div>
                  <div>
                    <label htmlFor="duration-per-question" className="flex cursor-pointer items-center gap-3">
                      <RadioGroupItem id="duration-per-question" value="per_question" />
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{t('time_limit_for_each_test_question_mm_ss')}</span>
                      <Input
                        value={timing.timeLimitPerQuestion}
                        onChange={(e) => updateTiming('timeLimitPerQuestion', e.target.value)}
                        placeholder={t('time_placeholder_mm_ss')}
                        className={`w-24 ${perQuestionInvalid ? 'border-destructive' : ''}`}
                        disabled={!isPerQuestion}
                        aria-invalid={perQuestionInvalid}
                      />
                    </label>
                    {perQuestionInvalid && <p className="ml-7 mt-1 text-sm text-destructive">{t('time_invalid_mmss')}</p>}
                  </div>
                </RadioGroup>
                </>
                )}

                <div className="border-t border-border pt-4">
                  <p className="mb-2 text-sm font-medium text-foreground">{t('test_activation_options')}</p>
                  <RadioGroup
                    value={timing.activationMethod}
                    onValueChange={(v) => {
                      // Block the client from ever landing in a "scheduled" state
                      // it isn't entitled to — open the upgrade dialog instead of
                      // letting the form fill out a config that Save can't apply.
                      if (v === 'scheduled' && !canScheduling) {
                        openUpgrade(FEATURES.TEST_SCHEDULING);
                        return;
                      }
                      updateTiming('activationMethod', v as ActivationMethod);
                    }}
                    name="activation"
                    className="gap-3"
                  >
                    <label htmlFor="activation-manual" className="flex cursor-pointer items-start gap-3">
                      <RadioGroupItem id="activation-manual" value="manual" className="mt-1" />
                      <div className="flex-1">
                        <span className="text-sm font-medium">{t('manual_test_activation')}</span>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span className="text-sm">{t('test_will_remain_active_for')}</span>
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <Input
                            type="number"
                            min={0}
                            value={timing.activeDays}
                            onChange={(e) => updateTiming('activeDays', e.target.value)}
                            className="w-16"
                            disabled={timing.activationMethod !== 'manual'}
                            aria-label={t('days')}
                          />
                          <span className="text-sm text-muted-foreground">{t('days')}</span>
                          <Input
                            type="number"
                            min={0}
                            value={timing.activeHours}
                            onChange={(e) => updateTiming('activeHours', e.target.value)}
                            className="w-16"
                            disabled={timing.activationMethod !== 'manual'}
                            aria-label={t('hours')}
                          />
                          <span className="text-sm text-muted-foreground">{t('hours')}</span>
                          <Input
                            type="number"
                            min={0}
                            value={timing.activeMinutes}
                            onChange={(e) => updateTiming('activeMinutes', e.target.value)}
                            className="w-16"
                            disabled={timing.activationMethod !== 'manual'}
                            aria-label={t('minutes')}
                          />
                          <span className="text-sm text-muted-foreground">{t('minutes')}</span>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{t('remain_active_indefinitely_hint')}</p>
                      </div>
                    </label>
                    <div className="flex items-start gap-3">
                      <RadioGroupItem id="activation-scheduled" value="scheduled" className="mt-1" />
                      <div className="flex-1">
                        <span className="inline-flex items-center gap-1.5 text-sm">
                          <label htmlFor="activation-scheduled" className="cursor-pointer">
                            {t('activation_in_a_set_time_period')}
                          </label>
                          {!canScheduling && (
                            <PremiumBadge
                              featureKey={FEATURES.TEST_SCHEDULING}
                              label={t('growth', 'Growth')}
                              tooltip={t('scheduling_premium_hint', 'Scheduling is a Growth feature')}
                            />
                          )}
                        </span>
                        {timing.activationMethod === 'scheduled' && (
                          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                              <label className="text-xs text-muted-foreground">{t('available_from')}</label>
                              <Input
                                type="datetime-local"
                                value={timing.availableFrom}
                                onChange={(e) => updateTiming('availableFrom', e.target.value)}
                              />
                            </div>
                            <div>
                              <label className="text-xs text-muted-foreground">{t('available_until')}</label>
                              <Input
                                type="datetime-local"
                                value={timing.availableUntil}
                                onChange={(e) => updateTiming('availableUntil', e.target.value)}
                                className={windowInvalid ? 'border-destructive' : ''}
                              />
                            </div>
                            {windowInvalid && (
                              <p className="text-sm text-destructive sm:col-span-2">{t('window_order_invalid')}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {!isExamTest && (
                <SettingRow
                  label={t('allow_answering_questions_in_any_order_and_moving_back_or_skipping_questions')}
                  description={isPerQuestion ? t('per_question_locks_navigation') : undefined}
                >
                  <Switch
                    checked={isPerQuestion ? false : timing.allowFlexibleNavigation}
                    onChange={(v) => updateTiming('allowFlexibleNavigation', v)}
                    disabled={isPerQuestion}
                  />
                </SettingRow>
                )}

                {!isExamTest && (
                <SettingRow
                  label={t('show_sub_questions_in_navigator')}
                  description={t('show_sub_questions_in_navigator_hint')}
                >
                  <Switch
                    checked={timing.showSubQuestionsInNavigator}
                    onChange={(v) => updateTiming('showSubQuestionsInNavigator', v)}
                  />
                </SettingRow>
                )}
              </CardContent>
            </Card>
          </section>

          {/* AI Auto-Grading */}
          <section ref={sectionRefs.aiGrading} data-section="aiGrading" id="aiGrading" className="scroll-mt-4">
            <Card>
              <CardContent className="p-6">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
                  {t('ai_auto_grading')}
                </h2>
                <SettingRow
                  label={t('automate_grading_open_questions')}
                  description={t('automate_grading_open_questions_desc')}
                >
                  <div className="flex items-center gap-2">
                    {!canAiGrading && (
                      <PremiumBadge
                        featureKey={FEATURES.AI_GRADING}
                        label={t('growth', 'Growth')}
                        tooltip={t('ai_grading_premium_hint', 'AI grading is a Growth feature')}
                      />
                    )}
                    <Switch
                      checked={canAiGrading && aiGrading.autoGradeOpenQuestions}
                      onChange={(v) => {
                        if (v && !canAiGrading) {
                          openUpgrade(FEATURES.AI_GRADING);
                          return;
                        }
                        updateAi('autoGradeOpenQuestions', v);
                      }}
                    />
                  </div>
                </SettingRow>

                {canAiGrading && aiGrading.autoGradeOpenQuestions && (
                  <SettingRow
                    stacked
                    label={t('ai_min_confidence_title')}
                    description={t('ai_min_confidence_desc')}
                  >
                    <RadioGroup
                      value={aiGrading.aiGradeMinConfidence}
                      onValueChange={(v) => updateAi('aiGradeMinConfidence', v as AiGradeMinConfidence)}
                      name="ai-min-confidence"
                      className="gap-3"
                    >
                      {AI_GRADE_MIN_CONFIDENCES.map((option) => (
                        <label
                          key={option.value}
                          htmlFor={`ai-min-confidence-${option.value}`}
                          className="flex cursor-pointer items-start gap-3"
                        >
                          <RadioGroupItem id={`ai-min-confidence-${option.value}`} value={option.value} className="mt-1" />
                          <span>
                            <span className="block text-sm font-medium text-foreground">{t(option.labelKey)}</span>
                            <span className="block text-xs leading-relaxed text-muted-foreground">{t(option.descKey)}</span>
                          </span>
                        </label>
                      ))}
                    </RadioGroup>
                  </SettingRow>
                )}

                <SettingRow
                  label={t('show_ai_feedback_to_candidates')}
                  description={t('show_ai_feedback_to_candidates_desc')}
                >
                  <Switch
                    checked={aiGrading.showAiFeedbackToCandidate}
                    onChange={(v) => updateAi('showAiFeedbackToCandidate', v)}
                  />
                </SettingRow>

                {isIeltsTest && (
                  <div data-setting="live-examiner">
                    <SettingRow label={t('live_examiner_followups')} description={t('live_examiner_followups_desc')}>
                      <div className="flex items-center gap-2">
                        {!canAiGrading && (
                          <PremiumBadge
                            featureKey={FEATURES.AI_GRADING}
                            label={t('growth', 'Growth')}
                            tooltip={t('ai_grading_premium_hint', 'AI grading is a Growth feature')}
                          />
                        )}
                        <Switch
                          checked={canAiGrading && aiGrading.liveExaminer}
                          onChange={(v) => {
                            if (v && !canAiGrading) {
                              openUpgrade(FEATURES.AI_GRADING);
                              return;
                            }
                            updateAi('liveExaminer', v);
                          }}
                        />
                      </div>
                    </SettingRow>
                  </div>
                )}
              </CardContent>
            </Card>
          </section>

          {/* Anti-cheating */}
          <section ref={sectionRefs.anticheat} data-section="anticheat" id="anticheat" className="scroll-mt-4">
            <Card>
              <CardContent className="p-6">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Shield className="h-4 w-4" />
                  {t('settings_section_anticheat')}
                </h2>
                <SettingRow label={t('shuffle_questions')} description={t('shuffle_questions_description')}>
                  <Switch checked={anti.shuffleQuestions} onChange={(v) => updateAnti('shuffleQuestions', v)} />
                </SettingRow>
                <SettingRow label={t('shuffle_answers')} description={t('shuffle_answers_description')}>
                  <Switch checked={anti.shuffleAnswers} onChange={(v) => updateAnti('shuffleAnswers', v)} />
                </SettingRow>
                <SettingRow label={t('anti_cheating_monitor')} description={t('anti_cheating_monitor_description')}>
                  <Switch checked={anti.antiCheatingMonitor} onChange={(v) => updateAnti('antiCheatingMonitor', v)} />
                </SettingRow>
                <SettingRow label={t('disable_right_click')} description={t('disable_right_click_description')}>
                  <Switch checked={anti.disableRightClick} onChange={(v) => updateAnti('disableRightClick', v)} />
                </SettingRow>
                <SettingRow label={t('disable_copy_paste')} description={t('disable_copy_paste_description')}>
                  <Switch checked={anti.disableCopyPaste} onChange={(v) => updateAnti('disableCopyPaste', v)} />
                </SettingRow>
                <SettingRow label={t('lockdown_browser')} description={t('lockdown_browser_description')}>
                  <Switch checked={anti.lockdownBrowser} onChange={(v) => updateAnti('lockdownBrowser', v)} />
                </SettingRow>
              </CardContent>
            </Card>
          </section>

          {/* Learner reports: the switch is part of the General state and saved with it. */}
          <section ref={sectionRefs.learnerReports} data-section="learnerReports" id="learnerReports" className="scroll-mt-4">
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-2 text-sm font-semibold text-foreground">{t('question_reports.setting_section')}</h2>
                <SettingRow label={t('question_reports.setting_label')} description={t('question_reports.setting_description')}>
                  <Switch
                    checked={general.questionReports}
                    onChange={(v) => setGeneral((p) => ({ ...p, questionReports: v }))}
                  />
                </SettingRow>
              </CardContent>
            </Card>
          </section>

          {/* Gamification (coming soon) */}
          <section ref={sectionRefs.gamification} data-section="gamification" id="gamification" className="scroll-mt-4">
            <Card className="border-2 border-dashed border-border">
              <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Gamepad2 className="h-[18px] w-[18px]" aria-hidden />
                </div>
                <p className="text-sm font-semibold text-foreground">{t('gamification')}</p>
                <p className="max-w-md text-sm text-muted-foreground">{t('gamification_coming_soon_desc')}</p>
              </CardContent>
            </Card>
          </section>
        </div>
      </div>

      <SaveBar
        visible={dirty}
        message={t('savebar_unsaved')}
        saveLabel={t('save_changes')}
        onSave={handleSave}
        discardLabel={t('discard')}
        onDiscard={handleDiscard}
        saving={saving}
      />
    </div>
  );
};

export default BasicSettingsPage;
